// The composer's audio dock: one non-modal bar at the bottom of the page for
// recording narration (in pieces), listening, syncing, and voice memos, so
// the author can read, scroll, listen and record at the same time. This hook
// is the state and the rules; AudioDock.tsx draws it, and BlockList shows
// its effect on the page (the current paragraph, tap targets, gutter chips).
//
// Modes:
// - record: pieces by default. Each paragraph read becomes a piece; Next (or
//   Space / ↓ / tapping a later paragraph) closes one and opens the next.
//   After Stop, review: play all, play or re-record one piece, record from a
//   paragraph on, Discard, Save (uploads finish, then the narration is set).
// - listen: play, scrub, follow; tap a paragraph to hear it. With Sync on (a
//   single-file narration), a tap or Space stamps "this paragraph starts now".
// - memo: one take for a voice memo beside a paragraph.

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import type { Block, Narration, NarrationClip } from '../../lib/types'
import {
  activeBlockAt,
  buildTimeline,
  fileNarration,
  isPieceNarration,
  narratedOrder,
  narrationFromClips,
  narrationTiming,
  nextInOrder,
  pieceEnd,
  startOf,
} from '../../lib/narration'
import { formatDuration, NARRATION_MAX_SECONDS, VOICE_MEMO_MAX_SECONDS, type CapturedAudio } from '../../lib/audio'
import type { NarrationSync } from '../../lib/useNarrationSync'
import { useNarrationPlayer } from '../../lib/useNarrationPlayer'
import type { AudioPlayer } from '../../lib/useAudioPlayer'
import { useBackend } from '../../lib/session'
import { RecordingSession, type DraftClip } from './recordingSession'
import { scrollToFollow, useFollowScroll } from './useFollowScroll'
import { NO_DOCK, type DockView, type GutterChip } from './BlockList'

export type DockMode = 'record' | 'listen' | 'memo'

/** Where a voice memo goes. */
export interface MemoTarget {
  /** A new memo goes in after this block (null: the end). */
  afterId: string | null
  /** Re-recording this existing memo instead. */
  voiceId?: string
  /** The column block it annotates (kept in view and highlighted). */
  anchorId: string | null
}

export interface DockOptions {
  narration: Narration | null
  blocks: Block[]
  /** The saved narration's player and marks (listen and sync). */
  sync: NarrationSync
  onNarration(next: Narration | null): void
  onMemo(target: MemoTarget, audio: CapturedAudio): void
  /** A short confirmation ("Narration saved"). */
  notify?(message: string): void
}

/** The session's clips as a narration the review player can play (local copies first). */
function draftNarration(clips: DraftClip[]): Narration | null {
  const ready = clips.filter((c) => c.status === 'ready')
  if (ready.length === 0) return null
  // jsdom and old engines can't make object URLs: an unplayable piece still keeps its time.
  return narrationFromClips(ready.map((c) => ({ id: c.id, blockId: c.blockId, src: c.localUrl || c.src || `demo:${c.id}`, duration: c.duration })))
}

const isTyping = (t: EventTarget | null) => {
  const el = t as HTMLElement | null
  return !!el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))
}

/** Scrolling keys (Space and ↓ are the dock's own in record and sync). */
const SCROLL_KEYS = ['PageUp', 'PageDown', 'Home', 'End', 'ArrowUp', 'ArrowDown', ' ']

export interface AudioDockController {
  mode: DockMode | null
  view: DockView
  follow: boolean
  setFollow(on: boolean): void
  /** Esc / Exit / Discard with nothing unsaved leaves at once; otherwise this asks first. */
  confirmExit: boolean
  requestExit(): void
  cancelExit(): void
  exit(): void
  /** Status for a moment ("Stop first to re-record earlier paragraphs"). */
  hint: string | null
  order: string[]
  blocks: Block[]
  narration: Narration | null
  // record + memo
  session: RecordingSession | null
  review: AudioPlayer
  /** The block the review player is reading. */
  reviewActiveId: string | null
  playPiece(blockId: string): void
  recordFromHere(): void
  rerecordPiece(): void
  start(): void
  save(): Promise<void>
  memo: MemoTarget | null
  // listen
  sync: NarrationSync
  syncing: boolean
  toggleSync(): void
  /** The paragraph Space would stamp next. */
  syncNext: string | null
  markNext(): void
  // entry points
  openRecord(): void
  openListen(opts?: { play?: boolean; sync?: boolean }): void
  openMemo(target: MemoTarget): void
  /** A file picked in the dock replaces the narration (or makes the memo), and the dock closes. */
  applyFile(audio: CapturedAudio, keepMarks: boolean): void
}

export function useAudioDock({ narration, blocks, sync, onNarration, onMemo, notify }: DockOptions): AudioDockController {
  const backend = useBackend()
  const [mode, setMode] = useState<DockMode | null>(null)
  const [memo, setMemo] = useState<MemoTarget | null>(null)
  const [follow, setFollow] = useState(true)
  const [confirmExit, setConfirmExit] = useState(false)
  const [hint, setHintState] = useState<string | null>(null)
  const [syncCursor, setSyncCursor] = useState<string | null>(null)
  const [session, setSession] = useState<RecordingSession | null>(null)
  const order = useMemo(() => narratedOrder(blocks), [blocks])
  const orderRef = useRef(order)
  orderRef.current = order

  // Re-render on every session change (phase, pieces, the elapsed tick).
  useSyncExternalStore(
    useCallback((fn: () => void) => session?.subscribe(fn) ?? (() => {}), [session]),
    () => session?.version ?? -1,
  )

  const hintTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const setHint = useCallback((text: string | null) => {
    clearTimeout(hintTimer.current)
    setHintState(text)
    if (text) hintTimer.current = setTimeout(() => setHintState(null), 3200)
  }, [])
  useEffect(() => () => clearTimeout(hintTimer.current), [])

  // ── review player (record and memo) ────────────────────────────────────
  const phase = session?.phase ?? 'idle'
  const reviewing = phase === 'review' || phase === 'idle' || phase === 'paused'
  const draftKey = session && reviewing ? session.clips.map((c) => `${c.id}:${c.status}:${c.duration}:${c.localUrl ?? c.src}`).join(',') : ''
  const draft = useMemo(() => (session && reviewing ? draftNarration(session.clips) : null), [draftKey]) // eslint-disable-line react-hooks/exhaustive-deps
  const review = useNarrationPlayer(draft)
  const reviewTimeline = useMemo(() => (draft ? buildTimeline(blocks, narrationTiming(draft)) : []), [draft, blocks])
  const reviewActiveId = review.playing || review.time > 0 ? activeBlockAt(reviewTimeline, review.time) : null

  // ── leaving ────────────────────────────────────────────────────────────
  const exit = useCallback(() => {
    session?.dispose()
    setSession(null)
    review.pause()
    sync.stopSync()
    sync.player.pause()
    setMode(null)
    setMemo(null)
    setConfirmExit(false)
    setHint(null)
  }, [session, review, sync, setHint])

  const requestExit = useCallback(() => {
    if (session?.unsaved && (mode === 'record' || mode === 'memo')) setConfirmExit(true)
    else exit()
  }, [session, mode, exit])

  // Leaving the composer mid-take still hands the microphone back.
  const sessionRef = useRef(session)
  sessionRef.current = session
  useEffect(() => () => sessionRef.current?.dispose(), [])

  // Closing the tab or reloading with unsaved pieces asks first (the browser's own prompt).
  const unsaved = !!session?.unsaved
  useEffect(() => {
    if (!unsaved) return
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [unsaved])

  // ── entering ───────────────────────────────────────────────────────────
  const newSession = useCallback(
    (initial: NarrationClip[], maxClipSeconds: number, memoAnchor?: string) =>
      new RecordingSession(initial, {
        order: () => (memoAnchor !== undefined ? [memoAnchor] : orderRef.current),
        upload: (blob) => backend.uploadAudio(blob),
        maxClipSeconds,
      }),
    [backend],
  )

  const openRecord = useCallback(() => {
    sync.stopSync()
    sync.player.pause()
    session?.dispose()
    const pieces = isPieceNarration(narration) ? narration.clips : []
    const s = newSession(pieces, NARRATION_MAX_SECONDS)
    if (pieces.length > 0) {
      // A piece narration opens on its pieces, ready to re-record one or record on.
      s.phase = 'review'
      s.currentId = null
    } else {
      s.currentId = orderRef.current[0] ?? null
    }
    setSession(s)
    setMemo(null)
    setMode('record')
    setFollow(true)
    setConfirmExit(false)
  }, [narration, newSession, session, sync])

  const openListen = useCallback(
    (opts: { play?: boolean; sync?: boolean } = {}) => {
      if (!narration) return
      session?.dispose()
      setSession(null)
      setMemo(null)
      setMode('listen')
      setFollow(true)
      setConfirmExit(false)
      if (opts.sync && !isPieceNarration(narration)) {
        const tl = sync.timeline
        const firstUnmarked = tl.findIndex((e) => !e.marked)
        setSyncCursor(firstUnmarked > 0 ? tl[firstUnmarked - 1].id : null)
        sync.startSync()
      } else if (opts.play) {
        sync.player.play()
      }
    },
    [narration, session, sync],
  )

  const openMemo = useCallback(
    (target: MemoTarget) => {
      sync.stopSync()
      sync.player.pause()
      session?.dispose()
      const anchor = target.anchorId ?? 'memo'
      const s = newSession([], VOICE_MEMO_MAX_SECONDS, anchor)
      s.currentId = anchor
      setSession(s)
      setMemo(target)
      setMode('memo')
      setFollow(true)
      setConfirmExit(false)
      if (target.anchorId) requestAnimationFrame(() => scrollToFollow(target.anchorId!))
    },
    [newSession, session, sync],
  )

  // ── record actions ─────────────────────────────────────────────────────
  const start = useCallback(() => {
    if (!session) return
    review.pause()
    if (mode === 'memo') {
      if (session.clips.length > 0) session.discard([])
      void session.begin(session.currentId ?? 'memo', null)
      return
    }
    const from = session.currentId ?? orderRef.current[0]
    if (from) void session.begin(from, null)
  }, [session, review, mode])

  const recordFromHere = start

  const rerecordPiece = useCallback(() => {
    if (!session?.currentId) return
    review.pause()
    void session.begin(session.currentId, pieceEnd(session.clips, orderRef.current, session.currentId))
  }, [session, review])

  const playPiece = useCallback(
    (blockId: string) => {
      if (!session || !draft) return
      session.setCurrent(blockId)
      const at = startOf(reviewTimeline, blockId)
      if (at === null) return
      review.seek(at)
      review.play()
    },
    [session, draft, reviewTimeline, review],
  )

  const save = useCallback(async () => {
    if (!session) return
    review.pause()
    const clips = await session.finalize()
    if (!clips) return
    if (mode === 'memo' && memo) {
      const c = clips[0]
      if (c) onMemo(memo, { src: c.src, duration: c.duration, peaks: c.peaks ?? [], recordedAt: c.recordedAt ?? new Date().toISOString() })
      notify?.(memo.voiceId ? 'Voice memo re-recorded' : 'Voice memo added')
    } else {
      onNarration(clips.length > 0 ? narrationFromClips(clips) : null)
      notify?.(clips.length > 0 ? `Narration saved · ${clips.length} piece${clips.length === 1 ? '' : 's'}` : 'Narration removed')
    }
    exit()
  }, [session, review, mode, memo, onMemo, onNarration, notify, exit])

  const applyFile = useCallback(
    (audio: CapturedAudio, keepMarks: boolean) => {
      if (mode === 'memo' && memo) {
        onMemo(memo, audio)
        notify?.(memo.voiceId ? 'Voice memo replaced' : 'Voice memo added')
      } else {
        onNarration(fileNarration(audio, keepMarks ? narration : null))
        notify?.('Narration replaced with the file')
      }
      exit()
    },
    [mode, memo, narration, onMemo, onNarration, notify, exit],
  )

  // ── listen / sync actions ──────────────────────────────────────────────
  const syncing = mode === 'listen' && sync.syncing
  const syncNext = syncing ? nextInOrder(order, syncCursor) : null

  const toggleSync = useCallback(() => {
    if (!narration || isPieceNarration(narration)) return
    if (sync.syncing) {
      sync.stopSync()
      return
    }
    const tl = sync.timeline
    const firstUnmarked = tl.findIndex((e) => !e.marked)
    setSyncCursor(firstUnmarked > 0 ? tl[firstUnmarked - 1].id : null)
    sync.startSync()
  }, [narration, sync])

  const stamp = useCallback(
    (blockId: string) => {
      sync.stamp(blockId)
      setSyncCursor(blockId)
    },
    [sync],
  )

  const markNext = useCallback(() => {
    if (syncNext) stamp(syncNext)
  }, [syncNext, stamp])

  // ── what the page shows ────────────────────────────────────────────────
  const onTapRecord = useCallback(
    (blockId: string) => {
      if (!session) return
      if (session.phase === 'recording') {
        if (!session.next(blockId)) setHint('Stop first to re-record earlier paragraphs.')
        return
      }
      if (session.phase === 'starting' || session.phase === 'finishing' || session.phase === 'saving') return
      session.setCurrent(blockId)
      if (review.playing) {
        const at = startOf(reviewTimeline, blockId)
        if (at !== null) review.seek(at)
      }
    },
    [session, review, reviewTimeline, setHint],
  )

  const view = useMemo<DockView>(() => {
    if (!mode) return NO_DOCK
    const chips = new Map<string, GutterChip>()
    if (mode === 'record' && session) {
      const byBlock = new Map<string, DraftClip[]>()
      for (const c of session.clips) byBlock.set(c.blockId, [...(byBlock.get(c.blockId) ?? []), c])
      for (const [blockId, cs] of byBlock) {
        const live = cs.some((c) => c.status === 'recording')
        const seconds = cs.reduce((t, c) => t + (c.status === 'recording' ? session.elapsed() : c.duration), 0)
        const label = formatDuration(seconds)
        const playable = !live && session.phase !== 'recording' && session.phase !== 'saving'
        chips.set(blockId, {
          label,
          live,
          ...(playable ? { onClick: () => playPiece(blockId), clickLabel: `Play this piece (${label})` } : {}),
        })
      }
      const current = session.phase === 'review' && review.playing && reviewActiveId ? reviewActiveId : session.currentId
      const recording = session.phase === 'recording'
      return {
        locked: true,
        currentId: current,
        chips,
        tap: {
          label: (s) => (recording ? `Next piece starts at “${s}”` : `Record from “${s}”`),
          onTap: onTapRecord,
        },
      }
    }
    if (mode === 'memo') return { locked: true, currentId: memo?.anchorId ?? null, chips }
    // listen
    if (syncing) {
      for (const e of sync.timeline) {
        if (!sync.isMarked(e.id)) continue
        const t = formatDuration(e.start)
        chips.set(e.id, {
          label: t,
          onClick: () => sync.playFrom(e.id),
          clickLabel: `Listen from ${t}`,
          onRemove: () => sync.unstamp(e.id),
          removeLabel: `Clear the mark at ${t}`,
        })
      }
    }
    return {
      locked: true,
      currentId: sync.activeBlockId,
      chips,
      tap: syncing
        ? { label: (s) => `Narration reaches “${s}” now`, onTap: stamp }
        : { label: (s) => `Listen from “${s}”`, onTap: (id) => sync.playFrom(id) },
    }
  }, [mode, session, session?.version, review.playing, reviewActiveId, memo, syncing, sync, stamp, onTapRecord, playPiece]) // eslint-disable-line react-hooks/exhaustive-deps

  // Follow what is being read or recorded.
  const followTarget = mode === null ? null : view.currentId
  useFollowScroll(followTarget, mode !== null, follow, () => setFollow(false))

  // ── keys ───────────────────────────────────────────────────────────────
  const keys = useRef<(e: KeyboardEvent) => boolean>(() => false)
  keys.current = (e) => {
    if (confirmExit) return false
    const key = e.key.length === 1 ? e.key.toLowerCase() : e.key
    if (key === 'Escape') {
      requestExit()
      return true
    }
    if (mode === 'record' || mode === 'memo') {
      const s = session
      if (!s) return false
      if (s.phase === 'recording' && (key === ' ' || key === 'ArrowDown') && mode === 'record') {
        s.next()
        return true
      }
      // A voice memo is one take: no pausing into pieces.
      if (key === 'p' && mode === 'record' && (s.phase === 'recording' || s.phase === 'paused')) {
        if (s.phase === 'recording') s.pause()
        else s.resume()
        return true
      }
      if (key === 'k' && (s.phase === 'review' || s.phase === 'idle' || s.phase === 'paused') && draft) {
        review.toggle()
        return true
      }
      if (key === ' ' && s.phase === 'idle' && mode === 'record') {
        start()
        return true
      }
      return false
    }
    if (mode === 'listen') {
      if (key === 'k') {
        sync.player.toggle()
        return true
      }
      if (key === ' ' && syncing) {
        markNext()
        return true
      }
    }
    return false
  }

  useEffect(() => {
    if (!mode) return
    let swallowed: string | null = null
    const onDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target) || e.defaultPrevented) return
      // Holding a key down doesn't skip through paragraphs.
      if (e.repeat) {
        if (swallowed === e.key) e.preventDefault()
        return
      }
      if (keys.current(e)) {
        e.preventDefault()
        swallowed = e.key
        return
      }
      // Scrolling by hand: stop following.
      if (SCROLL_KEYS.includes(e.key)) setFollow(false)
    }
    // A focused button would otherwise "click" on Space's keyup as well.
    const onUp = (e: KeyboardEvent) => {
      if (swallowed && e.key === swallowed) {
        e.preventDefault()
        swallowed = null
      }
    }
    window.addEventListener('keydown', onDown)
    window.addEventListener('keyup', onUp)
    return () => {
      window.removeEventListener('keydown', onDown)
      window.removeEventListener('keyup', onUp)
    }
  }, [mode])

  return {
    mode,
    view,
    follow,
    setFollow: (on: boolean) => {
      setFollow(on)
      if (on && view.currentId) scrollToFollow(view.currentId, true)
    },
    confirmExit,
    requestExit,
    cancelExit: () => setConfirmExit(false),
    exit,
    hint,
    order,
    blocks,
    narration,
    session,
    review,
    reviewActiveId,
    playPiece,
    recordFromHere,
    rerecordPiece,
    start,
    save,
    memo,
    sync,
    syncing,
    toggleSync,
    syncNext,
    markNext,
    openRecord,
    openListen,
    openMemo,
    applyFile,
  }
}
